<template>
    <div
        ref="root"
        :class="[
            $style.container,
            isVisible && $style.is_visible,
            isExpanded && $style.is_expanded,
            isPulsing && $style.is_pulsing,
        ]"
    >
        <img
            :class="$style.icon"
            :src="SaveIcon"
            alt=""
        />
        <div :class="$style.reveal">
            <div :class="$style.reveal_inner">
                <Typography
                    size="m"
                    type="label"
                    :responsive="false"
                    :class="$style.label"
                >
                    {{ $tr('gameContainer.fullscreen_promo_label') }}
                </Typography>
                <div :class="$style.buttons">
                    <OutlineButton
                        :class="[$style.button_override, $style.play_with_ad_button_override]"
                        color="white-20"
                        interfaceTarget="game-fullscreen-promo-play-with-ad-button"
                        size="s"
                        :label="$tr('gameContainer.fullscreen_promo_play_with_ad_button')"
                        @click="emit('playWithAd')"
                    />
                    <Button
                        :class="$style.button_override"
                        color="lime"
                        interfaceTarget="game-fullscreen-promo-login-button"
                        size="s"
                        :label="$tr('gameContainer.fullscreen_promo_login_button')"
                        @click="emit('login')"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core'
import { onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue'

import Button from '@/components/Buttons/Button.vue'
import OutlineButton from '@/components/Buttons/OutlineButton.vue'
import Typography from '@/components/Typography.vue'
import { timer } from '@/utils/helpers'

import SaveIcon from '../images/save.webp'

const APPEAR_DELAY = 1000
const EXPAND_DELAY = 500
const PULSE_DELAY = 2000
const COLLAPSE_DURATION = 260

const props = defineProps<{ visible: boolean }>()
const emit = defineEmits<{ shown: []; playWithAd: []; login: [] }>()

const isVisible = ref(false)
const isExpanded = ref(false)
const isPulsing = ref(false)

const ENTER_SEQUENCE = [
    [APPEAR_DELAY, isVisible],
    [EXPAND_DELAY, isExpanded],
    [PULSE_DELAY, isPulsing],
] as const

let generation = 0

async function playEnter() {
    generation += 1
    const gen = generation

    for (const [delay, phase] of ENTER_SEQUENCE) {
        await timer(delay)

        if (gen !== generation) {
            return
        }

        phase.value = true
    }
}

const root = useTemplateRef('root')
const { stop } = useIntersectionObserver(
    () => (isVisible.value ? root.value : null),
    ([entry]) => {
        if (entry?.isIntersecting) {
            stop()
            emit('shown')
        }
    },
)

async function playLeave() {
    generation += 1
    const gen = generation
    isPulsing.value = false
    isExpanded.value = false

    await timer(COLLAPSE_DURATION)

    if (gen !== generation) {
        return
    }

    isVisible.value = false
}

onMounted(() => {
    if (props.visible) {
        playEnter()
    }
})

watch(
    () => props.visible,
    (visible) => (visible ? playEnter() : playLeave()),
)

onUnmounted(() => {
    generation += 1
})
</script>

<style module>
.container {
    display: flex;
    align-items: center;
    padding: 4px;
    border-radius: 80px;
    background: #2f2e37;
    border: 1px solid #26252f;
    opacity: 0;
    transform: scale(0.6);
    transform-origin: center;
    pointer-events: none;
    transition:
        opacity 200ms cubic-bezier(0.16, 1, 0.3, 1),
        transform 400ms cubic-bezier(0.34, 1.56, 0.64, 1);
}

.is_visible {
    opacity: 1;
    transform: scale(1);
    pointer-events: auto;
}

.is_pulsing {
    animation: notif_pulse 3.8s cubic-bezier(0.65, 0, 0.35, 1) infinite;
}

.icon {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
}

.reveal {
    min-width: 0;
    display: grid;
    grid-template-columns: 0fr;
    overflow: hidden;
    transition: grid-template-columns 400ms cubic-bezier(0.65, 0, 0.35, 1);
}

.is_expanded .reveal {
    grid-template-columns: 1fr;
}

.reveal_inner {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 32px;
    opacity: 0;
    transition: opacity 250ms cubic-bezier(0.16, 1, 0.3, 1);
}

.is_expanded .reveal_inner {
    opacity: 1;
}

.label {
    margin-left: 8px;
    color: var(--white);
    text-wrap: nowrap;
}

.buttons {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.button_override {
    height: 32px;
}

.play_with_ad_button_override {
    --main-outline: #59585f;
}

@keyframes notif_pulse {
    0% {
        transform: translateX(0) scale(1);
    }
    6% {
        transform: translateX(8px) scale(1);
    }
    12% {
        transform: translateX(-6px) scale(1);
    }
    18% {
        transform: translateX(5px) scale(1);
    }
    23% {
        transform: translateX(-2px) scale(1);
    }
    27% {
        transform: translateX(0) scale(1);
    }
    100% {
        transform: translateX(0) scale(1);
    }
}

@media (prefers-reduced-motion: reduce) {
    .container {
        transform: none;
        transition: opacity 200ms linear;
    }

    .is_visible {
        transform: none;
    }

    .is_pulsing {
        animation: none;
    }

    .reveal,
    .reveal_inner {
        transition: none;
    }
}
</style>
